import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useAuthStore from '../store/authStore';
import UpdateCard from '../components/UpdateCard';
import './Updates.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SOURCE_INFO = {
  gmail: { icon: '📧', color: '#EA4335', label: 'Gmail' },
  outlook: { icon: '💼', color: '#0078D4', label: 'Outlook' },
  microsoft: { icon: '🔐', color: '#0078D4', label: 'Microsoft Security' },
  yahoo: { icon: '🔶', color: '#6001D2', label: 'Yahoo' },
  sendgrid: { icon: '📬', color: '#FF6B6B', label: 'SendGrid' },
  spamhaus: { icon: '🛡️', color: '#E74C3C', label: 'Spamhaus' },
  postmark: { icon: '✉️', color: '#1E90FF', label: 'Postmark' },
  apple: { icon: '🍎', color: '#555555', label: 'Apple Security' },
  mailgun: { icon: '📧', color: '#FF6B35', label: 'Mailgun' },
  cisa: { icon: '🛡️', color: '#0066CC', label: 'CISA Alerts' },
  proofpoint: { icon: '⚠️', color: '#E74C3C', label: 'Proofpoint' },
  edelivery: { icon: '📰', color: '#4169E1', label: 'eDelivery News' },
  bimi: { icon: '🎯', color: '#00A4EF', label: 'BIMI' },
  dmarc: { icon: '🔐', color: '#FF6B6B', label: 'DMARC' },
  ietf_standards: { icon: '📜', color: '#3B5998', label: 'IETF Standards' },
  auth_vendors: { icon: '🔑', color: '#8E44AD', label: 'Auth Vendors (dmarcian)' },
  auth_vendors_valimail: { icon: '🔑', color: '#8E44AD', label: 'Auth Vendors (Valimail)' },
  validity: { icon: '📊', color: '#00A19A', label: 'Validity' },
  wordtothewise: { icon: '🧠', color: '#5C4B99', label: 'Word to the Wise' },
  litmus: { icon: '🧪', color: '#F26522', label: 'Litmus' },
  reddit: { icon: '👽', color: '#FF4500', label: 'Reddit' },
  hackernews: { icon: '💬', color: '#FF6600', label: 'Hacker News' },
  exchange_team_blog: { icon: '💼', color: '#0078D4', label: 'Exchange Team Blog' },
  spamresource: { icon: '🚫', color: '#C0392B', label: 'Spam Resource' },
  dmarc_org: { icon: '🔏', color: '#2C3E50', label: 'DMARC.org' },
  bimigroup: { icon: '🖼️', color: '#16A085', label: 'BIMI Group' },
  google_workspace_status: { icon: '🟢', color: '#4285F4', label: 'Google Workspace Status' }
};

function Updates() {
  const [updates, setUpdates] = useState([]);
  const [filteredUpdates, setFilteredUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('');
  const [urgency, setUrgency] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { token } = useAuthStore();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    fetchUpdates();
  }, [source, urgency]);

  // Filter updates based on search query
  useEffect(() => {
    let filtered = updates;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(update =>
        update.title.toLowerCase().includes(query) ||
        update.description.toLowerCase().includes(query) ||
        (update.source && update.source.toLowerCase().includes(query))
      );
    }

    setFilteredUpdates(filtered);
  }, [searchQuery, updates]);

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      const params = {};
      if (source) params.source = source;
      if (urgency) params.urgency = urgency;
      
      const response = await axios.get(`${API_BASE}/updates`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpdates(response.data.updates);
    } catch (error) {
      console.error('Error fetching updates:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSourceStats = () => {
    const stats = {};
    updates.forEach(update => {
      stats[update.source] = (stats[update.source] || 0) + 1;
    });
    return stats;
  };

  const sourceStats = getSourceStats();

  return (
    <div className="updates-container">
      <div className="updates-header">
        <div className="header-content">
          <h1>📰 ISP Updates & Email Deliverability</h1>
          <p className="subtitle">Stay informed about email security, authentication, and ISP policy changes</p>
        </div>
        <button className="theme-toggle" onClick={() => setIsDarkMode(!isDarkMode)}>
          {isDarkMode ? '☀️ Light' : '🌙 Dark'}
        </button>
      </div>

      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-number">{updates.length}</span>
          <span className="stat-label">Total Updates</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{Object.keys(sourceStats).length}</span>
          <span className="stat-label">Sources</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{updates.filter(u => u.urgency === 'critical').length}</span>
          <span className="stat-label">Critical</span>
        </div>
      </div>

      <div className="search-bar">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search articles by title, description, or source..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button 
              className="search-clear"
              onClick={() => setSearchQuery('')}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        {searchQuery && (
          <div className="search-results-info">
            Found <strong>{filteredUpdates.length}</strong> result{filteredUpdates.length !== 1 ? 's' : ''} for "{searchQuery}"
          </div>
        )}
      </div>

      <div className="filters-container">
        <div className="filter-group">
          <label htmlFor="source-filter">Filter by Source</label>
          <select 
            id="source-filter"
            value={source} 
            onChange={(e) => setSource(e.target.value)}
            className="filter-select"
          >
            <option value="">All Sources</option>
            {Object.entries(SOURCE_INFO).map(([key, { icon, label }]) => (
              <option key={key} value={key}>{icon} {label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="urgency-filter">Filter by Urgency</label>
          <select 
            id="urgency-filter"
            value={urgency} 
            onChange={(e) => setUrgency(e.target.value)}
            className="filter-select"
          >
            <option value="">All Urgency Levels</option>
            <option value="critical">🔴 Critical</option>
            <option value="high">🟠 High</option>
            <option value="medium">🟡 Medium</option>
            <option value="low">🟢 Low</option>
          </select>
        </div>

        <button className="filter-reset" onClick={() => { setSource(''); setUrgency(''); setSearchQuery(''); }}>
          ✕ Reset All
        </button>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading updates...</p>
        </div>
      ) : filteredUpdates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h2>No updates found</h2>
          <p>{searchQuery ? 'Try adjusting your search query or filters' : 'Try adjusting your filters or check back later'}</p>
        </div>
      ) : (
        <div className="updates-grid">
          {filteredUpdates.map(update => (
            <UpdateCard key={update.id} update={update} sourceInfo={SOURCE_INFO[update.source]} />
          ))}
        </div>
      )}
    </div>
  );
}

export default Updates;
